'use client';

import { useEffect, useState } from 'react';
import s from './ResponseGenie.module.css';
import {
  MockIntent, MockKnowledge, MockVoice, MockChannel, MockHistory, MockHuman,
} from './Mocks';

/* ResponseGenie landing page — redesigned (Oct 2026).
   Copy/layout come from the approved design export; styles are page-local
   (ResponseGenie.module.css) and do NOT touch shared tokens/globals.
   The site Navbar/Footer come from the root layout; the page ends with
   <BookDemoSection/> (id="book-demo"), which every "Talk to Customer Success" CTA
   scrolls to. Images live in /public/ai-stack/responsegenie/v2/. */

const IMG = '/ai-stack/responsegenie/v2';

const STATS = [
  ['Up to 80%', 'Lift in agent efficiency, with no one rushing a reply to get there'],
  ['200+', 'Languages, with the cultural nuance and register a customer recognises'],
  ['Zero', 'Guesswork. Every reply is drafted from your knowledge base and brand guidelines'],
  ['Human', 'Review before send, on every single draft, by default'],
];

const CAPABILITIES = [
  ['Intent and emotion aware drafting', 'A frustrated third follow-up is not answered the way a first-time product question is. The draft shifts with what the customer is feeling and asking for.', MockIntent],
  ['Knowledge base grounding', 'Replies are built from your own documented policies, SLAs, processes and offers, so the facts in the message are the facts in your systems.', MockKnowledge],
  ['Brand voice control', 'Your tone guide, vocabulary, greetings, sign-offs and the things you never say are applied to every draft, across every agent on the floor.', MockVoice],
  ['Channel-aware formatting', 'A public X reply, an Instagram DM, a Google review response and a support email each get length, structure and formality suited to that surface.', MockChannel],
  ['Conversation history awareness', 'Prior tickets, earlier messages and repeat contacts are carried into the draft, so the customer is never asked to explain themselves again.', MockHistory],
  ['Human in the loop by default', 'Send, edit or regenerate stays with your agent, and a draft can be regenerated shorter, warmer, firmer or more formal in a click rather than rewritten from scratch.', MockHuman],
];

const Icon = ({ children, size = 17, sw = 2.1 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#0163FF" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

const CHANNELS = [
  {
    name: 'Social comments and posts',
    icon: <><path d="M21 12a9 9 0 1 1-4.2-7.6" /><path d="M8 11h8" /><path d="M8 15h5" /></>,
    examples: ['Instagram', 'Facebook', 'X', 'LinkedIn', 'YouTube'],
    adapts: 'Public visibility, brevity, de-escalation and a clear move to private channels where needed',
  },
  {
    name: 'Direct and private messages',
    icon: <path d="M4 5h16v11H8l-4 4Z" />,
    examples: ['Instagram DM', 'Facebook Messenger', 'LinkedIn Page DM', 'X DM'],
    adapts: "One to one tone, account specifics and continuity with the customer's earlier messages",
  },
  {
    name: 'Reviews and ratings',
    icon: <path d="m12 4 2.5 5.2 5.5.8-4 3.9 1 5.6-5-2.8-5 2.8 1-5.6-4-3.9 5.5-.8Z" />,
    examples: ['Google Business Profile', 'App store reviews', 'Marketplace', 'Travel review sites'],
    adapts: 'Star rating context, public reputation impact and a response future readers will see',
  },
  {
    name: 'Messaging apps',
    icon: <path d="M12 3a9 9 0 1 0 4.5 16.8L21 21l-1.2-4.4A9 9 0 0 0 12 3Z" />,
    examples: ['WhatsApp', 'Other supported messaging channels'],
    adapts: 'Conversational length, quick resolution and locale-appropriate language',
  },
  {
    name: 'Email and live chat',
    icon: <><path d="M3 6h18v12H3z" /><path d="m3 7 9 6 9-6" /></>,
    examples: ['Support inboxes', 'Web chat'],
    adapts: 'Structure, detail depth, policy citation and formal sign-off',
  },
];

const STEPS = [
  ['01', 'Read the message', 'Intent, emotion, urgency, language and channel are classified the moment the message lands.'],
  ['02', 'Retrieve the facts', 'The policy, SLA, process step and current offer that apply to this case are pulled from your knowledge base.'],
  ['03', 'Draft in your voice', 'The reply is composed against your brand guidelines and formatted for the channel it will be sent on.'],
  ['04', 'Review and send', 'The draft is waiting when your agent opens the conversation, with the sources it was built from.'],
];

const AUDIENCE = [
  ['Teams', 'Social care and community', 'Public comments, DMs and reviews answered fast and consistently, without a lead approving every line.',
    <><circle cx="12" cy="12" r="8" /><path d="M8.5 14a4.5 4.5 0 0 0 7 0" /><path d="M9 9.5h.01" /><path d="M15 9.5h.01" /></>],
  ['Teams', 'Contact centre operations', 'Shorter handling time on written channels, more of the queue cleared per shift, less variance between agents.',
    <><path d="M4 12a8 8 0 0 1 16 0" /><path d="M4 12v4a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 2Z" /><path d="M20 12v4a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 2Z" /></>],
  ['Teams', 'Brand and marketing', 'The tone guide finally applied where it matters most: in the replies customers actually read.',
    <><path d="M3 10v4h4l5 4V6l-5 4Z" /><path d="M17 9a4 4 0 0 1 0 6" /></>],
  ['Industry', 'BFSI', 'Replies grounded in documented policy and process, with sensitive and regulatory language routed for review.',
    <><path d="M4 10 12 5l8 5" /><path d="M6 10v8" /><path d="M12 10v8" /><path d="M18 10v8" /><path d="M4 19h16" /></>],
  ['Industry', 'Retail and e-commerce', "Order, refund, delivery and returns conversations answered with the current policy and the customer's own history.",
    <><path d="M4 7h16l-1.5 11H5.5Z" /><path d="M9 7a3 3 0 0 1 6 0" /></>],
  ['Industry', 'Telecom, travel and healthcare', 'High volume, high emotion queues where accuracy and empathy have to arrive in the same message.',
    <><path d="M12 5v14" /><path d="M5 12h14" /></>],
];

const FAQS = [
  ['What is ResponseGenie™?', "ResponseGenie™ is the AI response generation engine inside the Locobuzz customer experience platform. It reads the intent, emotion and history behind every incoming customer message, retrieves the relevant policy and facts from your connected knowledge base, and drafts a reply in your brand's voice for an agent to review and send."],
  ['How does ResponseGenie™ keep replies on brand?', 'Every draft is written against the brand guidelines and tone rules you connect, including vocabulary, greetings, sign-offs and the phrases you never use. The same rules apply to every agent, on every shift, so warmth and tone are held across a routine query, a sarcastic review or a backlash.'],
  ['Does ResponseGenie™ send replies automatically?', 'No. ResponseGenie™ is a copilot, not an autopilot. Every reply is a suggestion, and an agent decides whether to send it, edit it or regenerate it. Nothing reaches the customer without a person approving it.'],
  ['How much time does ResponseGenie™ save agents?', 'Teams see up to an 80% lift in agent efficiency, because the draft is already waiting when the conversation is opened. The time saved is in composing, checking policy and matching tone, not in rushing the reply.'],
  ['Can ResponseGenie™ handle complaints and negative sentiment?', 'Yes. Emotion, urgency and conversation history shape the draft, so a frustrated third follow-up is acknowledged and acted on rather than answered with a template. Sensitive and regulatory language can be routed for review before it is sent.'],
  ['How is ResponseGenie™ different from a generic AI chatbot?', "A generic chatbot answers from a general model and talks to the customer directly. ResponseGenie™ is grounded in your knowledge base and brand guidelines, works inside the agent's workflow, and never sends anything without a human approving it."],
];

function Head({ eyebrow, title, lead, left }) {
  return (
    <div className={`${s.head} ${left ? s.headLeft : ''}`}>
      <div className={s.eyebrow}>{eyebrow}</div>
      <h2 className={s.h2}>{title}</h2>
      {lead && <p className={s.lead}>{lead}</p>}
    </div>
  );
}

export default function ResponseGenieClient() {
  const [open, setOpen] = useState(0);
  const [active, setActive] = useState(0);
  const [held, setHeld] = useState(false);

  /* Coverage rows take turns highlighting; hovering a row pauses the cycle. */
  useEffect(() => {
    if (held) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % CHANNELS.length), 3200);
    return () => clearInterval(id);
  }, [held]);

  return (
    <div className={s.root}>
      {/* Hero */}
      <section data-screen-label="Hero" className={s.hero}>
        <div className={s.heroGrid}>
          <div>
            <h1 className={s.h1}>Accurate, on-brand replies at any volume, in any language</h1>
            <p className={s.heroSub}>What is ResponseGenie™? ResponseGenie™ is the AI response generation engine inside the Locobuzz customer experience platform. It reads the intent, emotion and history behind every customer message, pulls the right policy and product facts from your knowledge base, and drafts a brand-voice reply for an agent to review and send.</p>
            <div className={s.btnRow}>
              <a href="#book-demo" className={`${s.btn} ${s.btnPrimary}`}>Talk to Customer Success</a>
              <a href="mailto:sales@locobuzz.com" className={`${s.btn} ${s.btnGhost}`}>sales@locobuzz.com</a>
            </div>
          </div>
          <img className={s.heroImg} src={`${IMG}/hero-card.webp`} width={930} height={732} decoding="async" alt="A customer message tagged with intent and emotion, and the suggested reply ResponseGenie drafts from it" />
        </div>

        <div className={s.stats}>
          {STATS.map(([big, small]) => (
            <div key={big} className={s.stat}>
              <div className={s.statBig}>{big}</div>
              <div className={s.statSmall}>{small}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What it does */}
      <section data-screen-label="What it does" className={`${s.section} ${s.sectionAlt}`}>
        <div className={s.inner}>
          <Head
            eyebrow="Signals, not keywords"
            title="What ResponseGenie™ does on every conversation."
            lead="Six capabilities that turn a blank reply box into a finished draft an agent can stand behind."
          />
          <div className={s.caps}>
            {CAPABILITIES.map(([title, body, Mock]) => (
              <div key={title} className={s.capCard}>
                <h3 className={s.capTitle}>{title}</h3>
                <p className={s.capCopy}>{body}</p>
                <div className={s.capMock}><Mock /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section data-screen-label="Coverage" className={s.section}>
        <div className={s.inner}>
          <Head
            left
            eyebrow="Coverage"
            title="Where ResponseGenie™ replies."
            lead="Wherever Locobuzz listens and responds, ResponseGenie™ drafts. One brand voice across public and private conversations."
          />
          <div className={s.chan}>
            <div className={`${s.chanRow} ${s.chanHead}`} aria-hidden="true">
              <div>Channel type</div>
              <div>Examples</div>
              <div>What the draft adapts for</div>
            </div>
            <div className={s.chanList}>
              {CHANNELS.map((c, i) => (
                <div
                  key={c.name}
                  className={`${s.chanRow} ${s.chanItem} ${i === active ? s.chanOn : ''}`}
                  onMouseEnter={() => { setHeld(true); setActive(i); }}
                  onMouseLeave={() => setHeld(false)}
                >
                  <span className={s.chanBar} aria-hidden="true" />
                  <div className={s.chanName}>
                    <span className={s.chanIcon}><Icon>{c.icon}</Icon></span>
                    <span>{c.name}</span>
                  </div>
                  <div className={s.chips}>
                    {c.examples.map((e) => <span key={e} className={s.chip}>{e}</span>)}
                  </div>
                  <div className={s.chanAdapt}>
                    <p className={s.chanCopy}>{c.adapts}</p>
                    <span className={s.chanDot}>DRAFTING</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section data-screen-label="How it works" className={`${s.section} ${s.sectionAlt}`}>
        <div className={s.inner}>
          <Head
            eyebrow="How it works"
            title="From incoming message to on-brand reply, in four steps."
            lead="Everything below happens in real time, before an agent has read a word."
          />
          <div className={s.flowWrap}>
            <img className={s.flowImg} src={`${IMG}/flow.webp`} width={2640} height={866} loading="lazy" decoding="async" alt="A customer message is classified for urgency, intent, emotion and language, matched against the knowledge base for policies, SLAs and offers, drafted against brand guidelines, and reviewed before send" />
            <span className={s.flowRail} aria-hidden="true" />
            <span className={s.flowTravel} aria-hidden="true" />
          </div>
          <div className={s.steps}>
            {STEPS.map(([n, title, body]) => (
              <div key={n} className={s.step}>
                <div className={s.stepHead}>
                  <span className={s.stepNum}>{n}</span>
                  <h3 className={s.stepTitle}>{title}</h3>
                </div>
                <p className={s.stepCopy}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it is for */}
      <section data-screen-label="Who it is for" className={s.section}>
        <div className={s.inner}>
          <Head
            left
            eyebrow="Who it is for"
            title="Built for the teams that answer customers all day."
            lead="ResponseGenie™ supports high volume, high sensitivity and multi-market response operations across regulated and consumer-facing industries."
          />
          <div className={s.who}>
            {AUDIENCE.map(([kind, title, body, icon]) => (
              <div key={title} className={s.whoCard}>
                <div className={s.whoKind}>{kind}</div>
                <div className={s.whoHead}>
                  <span className={s.whoIcon}><Icon size={20} sw={2.2}>{icon}</Icon></span>
                  <h3 className={s.whoTitle}>{title}</h3>
                </div>
                <p className={s.whoCopy}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-screen-label="FAQ" className={`${s.section} ${s.sectionAlt}`}>
        <div className={s.faqInner}>
          <Head eyebrow="FAQ" title="Frequently asked questions." />
          <div className={s.faq}>
            {FAQS.map(([q, a], i) => {
              const isOpen = open === i;
              return (
                <div key={q} className={`${s.faqItem} ${isOpen ? s.faqOpen : ''}`}>
                  <button type="button" className={s.faqBtn} aria-expanded={isOpen} aria-controls={`rg-faq-${i}`} onClick={() => setOpen(isOpen ? -1 : i)}>
                    <span className={s.faqQ}>{q}</span>
                    <span aria-hidden="true" className={s.faqIcon}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0163FF" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </span>
                  </button>
                  {/* Always in the DOM (hidden when closed) so the answers stay crawlable. */}
                  <p id={`rg-faq-${i}`} className={s.faqAns} hidden={!isOpen}>{a}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-screen-label="Final CTA" className={s.cta}>
        <div className={s.ctaInner}>
          <h2 className={s.ctaH2}>Accurate, on-brand replies at any volume, in any language</h2>
          <div className={s.ctaBtns}>
            <a href="#book-demo" className={`${s.btn} ${s.btnPrimary}`}>Talk to Customer Success</a>
            <a href="mailto:sales@locobuzz.com" className={`${s.btn} ${s.btnGhost}`}>sales@locobuzz.com</a>
          </div>
        </div>
      </section>
    </div>
  );
}
